import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import api from '../../utils/api';
import { useAuthStore } from '../../store/authStore';
import { Camera, Save, User } from 'lucide-react';

export default function ProfilePage() {
  const { user, updateUser } = useAuthStore();
  const fileRef = useRef();

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    if (user) reset({
      full_name: user.full_name,
      phone: user.phone,
      city: user.city,
      country: user.country,
      bio: user.bio,
      role: user.role,
    });
  }, [user, reset]);

  const updateMutation = useMutation(
    (data) => api.put('/users/profile', data),
    {
      onSuccess: (res) => {
        updateUser(res.data.user);
        alert('تم تحديث الملف الشخصي بنجاح');
      }
    }
  );

  const avatarMutation = useMutation(
    (file) => {
      const fd = new FormData();
      fd.append('avatar', file);
      return api.post('/users/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    {
      onSuccess: (res) => updateUser({ avatar: res.data.avatar })
    }
  );

  const passwordMutation = useMutation(
    (data) => api.put('/auth/change-password', data),
    { onSuccess: () => alert('تم تغيير كلمة المرور بنجاح') }
  );

  const { register: regPw, handleSubmit: handlePw, reset: resetPw, watch: watchPw } = useForm();

  return (
    <div className="max-w-2xl mx-auto space-y-6" dir="rtl">
      <h1 className="text-2xl font-bold text-white">الملف الشخصي</h1>

      {/* Avatar */}
      <div className="glass rounded-2xl p-6 flex items-center gap-5">
        <div className="relative">
          <div className="w-20 h-20 rounded-full overflow-hidden bg-slate-700 flex items-center justify-center">
            {user?.avatar ? (
              <img src={`${process.env.REACT_APP_API_URL}/${user.avatar}`} alt="" className="w-full h-full object-cover" />
            ) : (
              <User size={32} className="text-slate-500" />
            )}
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            className="absolute -bottom-1 -left-1 w-7 h-7 bg-primary-500 rounded-full flex items-center justify-center hover:bg-primary-600"
          >
            <Camera size={13} className="text-white" />
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && avatarMutation.mutate(e.target.files[0])} />
        </div>
        <div>
          <h3 className="font-bold text-white">{user?.full_name}</h3>
          <p className="text-slate-400 text-sm">{user?.email}</p>
          <p className="text-primary-400 text-xs mt-1">
            {{ owner: 'مالك/مزود', renter: 'مستأجر', both: 'مالك ومستأجر', admin: 'مشرف' }[user?.role]}
          </p>
        </div>
      </div>

      {/* Profile Form */}
      <form onSubmit={handleSubmit(d => updateMutation.mutate(d))} className="glass rounded-2xl p-6 space-y-5">
        <h3 className="font-bold text-white border-b border-slate-700 pb-3">البيانات الشخصية</h3>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm text-slate-400 mb-2">الاسم الكامل *</label>
            <input {...register('full_name', { required: true })} className="form-input w-full" />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">رقم الهاتف</label>
            <input type="tel" {...register('phone')} className="form-input w-full" placeholder="+966500000000" />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">نوع الحساب</label>
            <select {...register('role')} className="form-input w-full">
              <option value="renter">مستأجر فقط</option>
              <option value="owner">مالك/مزود فقط</option>
              <option value="both">مالك ومستأجر</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">المدينة</label>
            <input {...register('city')} className="form-input w-full" />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">الدولة</label>
            <input {...register('country')} className="form-input w-full" />
          </div>
          <div className="col-span-2">
            <label className="block text-sm text-slate-400 mb-2">نبذة تعريفية</label>
            <textarea {...register('bio')} rows={3} className="form-input w-full resize-none" placeholder="اكتب نبذة مختصرة عنك..." />
          </div>
        </div>

        <button type="submit" disabled={updateMutation.isLoading} className="w-full btn-primary flex items-center justify-center gap-2">
          <Save size={18} />
          {updateMutation.isLoading ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}
        </button>
      </form>

      {/* Change Password */}
      <form onSubmit={handlePw(d => { passwordMutation.mutate(d); resetPw(); })} className="glass rounded-2xl p-6 space-y-5">
        <h3 className="font-bold text-white border-b border-slate-700 pb-3">تغيير كلمة المرور</h3>
        <div>
          <label className="block text-sm text-slate-400 mb-2">كلمة المرور الحالية</label>
          <input type="password" {...regPw('currentPassword', { required: true })} className="form-input w-full" />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-2">كلمة المرور الجديدة</label>
          <input type="password" {...regPw('newPassword', { required: true, minLength: 8 })} className="form-input w-full" />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-2">تأكيد كلمة المرور الجديدة</label>
          <input type="password" {...regPw('confirmPassword', { validate: v => v === watchPw('newPassword') || 'كلمتا المرور غير متطابقتين' })} className="form-input w-full" />
        </div>
        {passwordMutation.isError && <p className="text-red-400 text-sm">كلمة المرور الحالية غير صحيحة</p>}
        <button type="submit" disabled={passwordMutation.isLoading} className="w-full btn-secondary">
          {passwordMutation.isLoading ? 'جارٍ التغيير...' : 'تغيير كلمة المرور'}
        </button>
      </form>
    </div>
  );
}
